<template>
  <div class="dropdown-selector" ref="selectRef" @click.stop="toggle">
    <div
      class="dropdown-selector__label"
      :class="{ '_open': isLabelActive }"
    >
      {{ label }}
    </div>

    <div class="dropdown-selector__value">
      <input
        type="text"
        v-model="inputValue"
        class="dropdown-selector__input"
        :placeholder="placeholder"
        @keydown.enter.prevent="select(filteredOptions[0])"
      />
    </div>

    <vDown
      class="vicon-black dropdown-selector__arrow"
      :class="{ '_open': isOpened }"
      @click.stop="toggle"
    />

    <div
      v-if="isOpened"
      class="dropdown-selector__menu"
      :class="{ '_open': isOpened }"
    >
      <div
        v-for="option in filteredOptions"
        :key="option.id"
        class="dropdown-selector__option"
        :class="{
          _selected: isMultiple
            ? normalizedChoosed.includes(String(option.id))
            : normalizedChoosed === String(option.id)
        }"
        @click.stop="select(option)"
      >
        <svg class="checkbox__check" v-if="isMultiple" width="24" height="24" viewBox="0 0 24 24">
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>

        <span class="dropdown-selector__option-name">{{ option.name }}</span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" src="./MySelect.scss"></style>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import vDown from '@/assets/vicons/chevron-down.vue'

let activeSelect = null

const props = defineProps({
  list: {
    type: Array,
    default: () => []
  },
  choosed: {
    type: [String, Array],
    default: ''
  },
  mode: {
    type: String,
    default: 'single' // 'single' | 'multiple'
  },
  placeholder: String,
  label: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['input'])

const isOpened = ref(false)
const selectRef = ref(null)
const inputValue = ref('')
const search = ref('')
const filteredOptions = ref([])

const isMultiple = computed(() => props.mode === 'multiple')

const normalizedChoosed = computed(() => {
  if (isMultiple.value) {
    return Array.isArray(props.choosed) ? props.choosed : []
  }
  return props.choosed || ''
})

const isLabelActive = computed(() =>
  isOpened.value || inputValue.value.trim() !== ''
)

const throttle = (fn, delay = 120) => {
  let timer = null
  return (...args) => {
    if (timer) return
    timer = setTimeout(() => {
      fn(...args)
      timer = null
    }, delay)
  }
}

const doFilter = () => {
  const q = inputValue.value.trim().toLowerCase()
  search.value = q

  if (!q) {
    filteredOptions.value = props.list
    return
  }

  filteredOptions.value = props.list.filter(o =>
    o.name.toLowerCase().includes(q)
  )
}

const throttledFilter = throttle(doFilter, 120)

watch(inputValue, () => {
  throttledFilter()
})

watch(
  () => props.list,
  () => {
    filteredOptions.value = props.list
  },
  { immediate: true }
)

const select = (option) => {
  if (!option) return

  const id = String(option.id)

  if (isMultiple.value) {
    const current = Array.isArray(props.choosed)
      ? [...props.choosed]
      : []

    const index = current.indexOf(id)

    if (index > -1) {
      current.splice(index, 1)
    } else {
      current.push(id)
    }

    emit('input', current)

    inputValue.value = ''
    search.value = ''
    filteredOptions.value = props.list

    return
  }

  // для сингла
  emit('input', id)
  inputValue.value = option.name
  search.value = ''
  filteredOptions.value = props.list
  close()
}

const close = () => {
  isOpened.value = false
  search.value = ''
  filteredOptions.value = props.list

  if (activeSelect === selectRef.value) {
    activeSelect = null
  }
}

const open = () => {
  if (activeSelect && activeSelect !== selectRef.value) {
    activeSelect.__close()
  }

  isOpened.value = true
  activeSelect = selectRef.value
  filteredOptions.value = props.list
}

const toggle = (e) => {
  e.stopPropagation()

  if (isOpened.value) close()
  else open()
}

const onGlobalPointerDown = (e) => {
  if (!activeSelect) return
  if (!activeSelect.contains(e.target)) {
    activeSelect.__close()
  }
}

watch(
  () => props.choosed,
  (val) => {
    if (isMultiple.value) {
      if (!Array.isArray(val) || !val.length) {
        inputValue.value = ''
        return
      }

      const names = props.list
        .filter(o => val.includes(String(o.id)))
        .map(o => o.name)

      inputValue.value = names.join(', ')
      return
    }

    if (!val) {
      inputValue.value = ''
      return
    }

    const option = props.list.find(o => String(o.id) === val)
    inputValue.value = option ? option.name : ''
  },
  { immediate: true }
)

watch(inputValue, (val) => {
  if (!val) {
    search.value = ''
    filteredOptions.value = props.list

    if (isMultiple.value) {
      emit('input', [])
    } else {
      emit('input', '')
    }
  }
})

onMounted(() => {
  document.addEventListener('mousedown', onGlobalPointerDown, true)
  if (selectRef.value) {
    selectRef.value.__close = close
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onGlobalPointerDown, true)
  if (activeSelect === selectRef.value) {
    activeSelect = null
  }
})
</script>
